/**
 * Environment Variable Validation Utility
 *
 * Validates required environment variables and provides helpful error messages
 */

interface EnvironmentConfig {
  VITE_SUPABASE_URL: string;
  VITE_SUPABASE_ANON_KEY: string;
  VITE_API_BASE_URL?: string;
  VITE_APP_ENV?: string;
}

interface ValidationError {
  variable: string;
  message: string;
  suggestion?: string;
}

/**
 * Required environment variables for the application to function
 */
const REQUIRED_ENV_VARS: Array<keyof EnvironmentConfig> = [
  'VITE_SUPABASE_URL',
  'VITE_SUPABASE_ANON_KEY',
];

/**
 * Validates that all required environment variables are present and valid
 * @returns Array of validation errors, empty if all valid
 */
function validateEnvironmentVariables(): ValidationError[] {
  const errors: ValidationError[] = [];
  const env = import.meta.env;

  // Check required variables
  for (const variable of REQUIRED_ENV_VARS) {
    const value = env[variable];

    if (!value || value.trim() === '') {
      errors.push({
        variable,
        message: `Required environment variable ${variable} is missing`,
        suggestion: getVariableSuggestion(variable),
      });
    }
  }

  return errors;
}

/**
 * Provides helpful suggestions for missing environment variables
 */
function getVariableSuggestion(variable: keyof EnvironmentConfig): string {
  switch (variable) {
    case 'VITE_SUPABASE_URL':
      return 'Add VITE_SUPABASE_URL to your .env.local file';
    case 'VITE_SUPABASE_ANON_KEY':
      return 'Add VITE_SUPABASE_ANON_KEY to your .env.local file';
    default:
      return `Add ${variable} to your .env.local file`;
  }
}

/**
 * Validates environment on application startup
 * Throws an error if required variables are missing
 */
export function validateEnvironmentOnStartup(): void {
  const errors = validateEnvironmentVariables();

  if (errors.length > 0) {
    // Use console for critical startup errors since Logger might not be initialized
    if (import.meta.env.DEV) {
      // Startup validation may run before Logger's imports settle.
      console.error('Environment configuration errors:', errors);
    }

    if (!import.meta.env.DEV) {
      throw new Error('Missing required environment variables');
    }
  }
}
